// ✅ src/components/screens/CheckoutScreen.js (FULL - PAY FIRST → PLACE ORDER)
// ✅ Supports BOTH:
//    1) Buy Now (state.productId present) -> single productSnapshot
//    2) Cart Checkout (no productId, has storeId) -> itemsSnapshot[] + real subtotal
// ✅ Mobile picker bottom-sheet stays the same
// ✅ Cart per store: cart_items_v1:${storeId}
// ✅ CHANGE REQUESTED (DONE):
//    - ❌ Removed DEBUG completely
//    - ❌ Removed "Back to Browse"
//    - ✅ Phone + Reference are REQUIRED but now collected in a POPUP when user clicks "Place Order"
// ✅ NEW CHANGE (YOUR REQUEST):
//    - ✅ If user has NO saved addresses → show a POPUP telling them to go add an address (instead of inline warning)
// ✅ FIX KEPT:
//    - Cart checkout saves selected SIZE/COLOR into orders.itemsSnapshot.options
// ✅ DISCOUNT LOGIC KEPT:
//    - salePrice (>0) treated as final
//    - otherwise apply hasDiscount/discountPercent

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db, storage } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";

/* ---------------- helpers ---------------- */
function safe(v) {
  return String(v ?? "").trim();
}
function money(n, cur = "GHS") {
  const num = Number(n);
  if (Number.isNaN(num)) return `${cur} —`;
  return `${cur} ${num.toLocaleString()}`;
}

/**
 * ✅ DISCOUNT-AWARE PRICE
 * - If p.salePrice exists and > 0 => return it (already-final)
 * - Else use base price candidates, then apply:
 *   hasDiscount OR discountPercent > 0
 */
function getPriceValue(p) {
  const sale = Number(p?.salePrice);
  if (!Number.isNaN(sale) && sale > 0) return sale;

  const candidates = [
    p?.price,
    p?.amount,
    p?.unitPrice,
    p?.priceGHS,
    p?.priceValue,
  ];

  let base = 0;
  for (const c of candidates) {
    const n = Number(c);
    if (!Number.isNaN(n) && n > 0) {
      base = n;
      break;
    }
  }

  if (!base) {
    const zero = Number(p?.price);
    if (!Number.isNaN(zero)) base = zero;
  }

  const hasDiscount = !!p?.hasDiscount || Number(p?.discountPercent || 0) > 0;
  const discountPercent = Math.max(
    0,
    Math.min(95, Number(p?.discountPercent || 0))
  );

  if (!hasDiscount || discountPercent <= 0) return base;

  return Math.round((base * (100 - discountPercent)) / 100);
}

/* ---------------- cart (PER STORE) ---------------- */
function cartKey(storeId) {
  return `cart_items_v1:${String(storeId || "unknown")}`;
}
function loadCart(storeId) {
  try {
    if (!safe(storeId)) return [];
    const raw = localStorage.getItem(cartKey(storeId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function saveCart(storeId, items) {
  try {
    if (!safe(storeId)) return;
    localStorage.setItem(cartKey(storeId), JSON.stringify(items || []));
  } catch {}
}
function cartCount(items) {
  try {
    return (items || []).reduce(
      (sum, it) => sum + Math.max(0, Number(it?.qty || 0)),
      0
    );
  } catch {
    return 0;
  }
}

/* ---------------- loaders ---------------- */
async function loadStorePaymentMethods(storeId) {
  if (!storeId) return [];
  try {
    const subSnap = await getDocs(
      collection(db, "stores", storeId, "paymentMethods")
    );
    const sub = subSnap.docs
      .map((d) => ({ id: d.id, ...(d.data() || {}) }))
      .filter((m) => m?.isEnabled !== false);
    if (sub.length) return sub;
  } catch {}
  try {
    const qy = query(
      collection(db, "paymentMethods"),
      where("storeId", "==", storeId)
    );
    const rootSnap = await getDocs(qy);
    return rootSnap.docs
      .map((d) => ({ id: d.id, ...(d.data() || {}) }))
      .filter((m) => m?.isEnabled !== false);
  } catch {
    return [];
  }
}

async function loadStoreDeliveryOptions(storeId) {
  if (!storeId) return [];
  try {
    const subSnap = await getDocs(
      collection(db, "stores", storeId, "deliveryOptions")
    );
    const sub = subSnap.docs
      .map((d) => ({ id: d.id, ...(d.data() || {}) }))
      .filter((o) => o?.isEnabled !== false);
    if (sub.length) return sub;
  } catch {}
  try {
    const qy = query(
      collection(db, "deliveryOptions"),
      where("storeId", "==", storeId)
    );
    const rootSnap = await getDocs(qy);
    return rootSnap.docs
      .map((d) => ({ id: d.id, ...(d.data() || {}) }))
      .filter((o) => o?.isEnabled !== false);
  } catch {
    return [];
  }
}

/* ---------------- payment method formatting ---------------- */
function iconEmojiFallback(type) {
  const t = safe(type).toUpperCase();
  if (t === "MOMO") return "🟨";
  if (t === "BANK") return "🏦";
  if (t === "CASH") return "💵";
  return "💳";
}

function formatMethodRightTextAuto(m) {
  const type = safe(m?.type).toUpperCase();
  const d = m?.details || {};
  if (type === "MOMO") return safe(d?.number || d?.phone || "");
  if (type === "BANK") return safe(d?.accountNumber || d?.number || "");
  if (type === "CASH") return "Pay on pickup";
  return "";
}

function formatMethodSub(m) {
  const type = safe(m?.type).toUpperCase();
  const label = safe(m?.label) || type;
  const d = m?.details || {};

  const pickName = () =>
    safe(
      d?.name ||
        d?.momoName ||
        d?.momo_name ||
        d?.fullName ||
        d?.accountName ||
        d?.account_name ||
        d?.receiverName ||
        d?.receiver_name ||
        d?.registeredName ||
        d?.registered_name
    );

  if (type === "MOMO") {
    const net = safe(d?.network).toUpperCase();
    const number = safe(d?.number || d?.phone);
    const name = pickName();

    const parts = [label];
    if (net) parts.push(net);
    if (name) parts.push(name);
    if (number) parts.push(number);

    return parts.join(" • ").trim();
  }

  if (type === "BANK") {
    const bank = safe(d?.bankName || d?.bank).trim();
    const acc = safe(d?.accountNumber || d?.number).trim();
    const name = pickName();

    const parts = [label];
    if (bank) parts.push(bank);
    if (name) parts.push(name);
    if (acc) parts.push(acc);

    return parts.join(" • ").trim();
  }

  if (type === "CASH") {
    const note = safe(d?.note) || "Pay on pickup / meetup";
    return `${label} • ${note}`.trim();
  }

  return label;
}

function formatAddressOneLine(a) {
  if (!a) return "";
  const parts = [
    safe(a.label),
    safe(a.region),
    safe(a.city),
    safe(a.addressLine1),
    safe(a.landmark),
  ].filter(Boolean);
  return parts.join(" • ");
}

/* ✅ DELIVERY MATCHING (zones/areas + quote mode) */
function deliveryPricingMode(d) {
  return safe(d?.pricingMode).toUpperCase() || "FIXED"; // FIXED | QUOTE
}
function norm(s) {
  return safe(s).toLowerCase();
}
function listAreas(d) {
  const arr = Array.isArray(d?.areas) ? d.areas : [];
  return arr.map((x) => norm(x)).filter(Boolean);
}
function deliveryMatchesAddress(d, addr) {
  if (!d || !addr) return false;
  const areas = listAreas(d);
  if (!areas.length) return true;
  const city = norm(addr?.city);
  const region = norm(addr?.region);
  const label = norm(addr?.label);

  return areas.some((a) => {
    if (!a) return false;
    if (a === city || a === region) return true;
    if (city.includes(a) || region.includes(a) || label.includes(a)) return true;
    return false;
  });
}
function pickBestDelivery(deliveryOptions, addr) {
  const list = Array.isArray(deliveryOptions) ? deliveryOptions : [];
  if (!list.length) return null;

  const matched = addr
    ? list.filter((d) => deliveryMatchesAddress(d, addr))
    : list.slice();

  const fixed = matched.filter((d) => deliveryPricingMode(d) !== "QUOTE");
  if (fixed[0]) return fixed[0];
  if (matched[0]) return matched[0];
  return list[0];
}

/* ---------------- tiny inline svg icons ---------------- */
function Svg({ children, size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      {children}
    </svg>
  );
}
const Icons = {
  User: (
    <Svg>
      <path
        d="M12 12c2.761 0 5-2.239 5-5S14.761 2 12 2 7 4.239 7 7s2.239 5 5 5Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M4 22c0-4.418 3.582-8 8-8s8 3.582 8 8"
        stroke="currentColor"
        strokeWidth="2"
      />
    </Svg>
  ),
  Phone: (
    <Svg>
      <path
        d="M22 16.92v3a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 3 5.18 2 2 0 0 1 5 3h3a2 2 0 0 1 2 1.72c.12.86.31 1.7.57 2.5a2 2 0 0 1-.45 2.11L9.09 10.91a16 16 0 0 0 4 4l1.58-1.03a2 2 0 0 1 2.11-.45c.8.26 1.64.45 2.5.57A2 2 0 0 1 22 16.92Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </Svg>
  ),
  Note: (
    <Svg>
      <path d="M4 4h16v16H4V4Z" stroke="currentColor" strokeWidth="2" />
      <path
        d="M8 8h8M8 12h8M8 16h5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Svg>
  ),
  ChevronDown: (
    <Svg size={16}>
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  ),
  Upload: (
    <Svg>
      <path
        d="M12 16V4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M7 9l5-5 5 5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 16v4H4v-4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Svg>
  ),
};

/* ---------------- MOBILE BOTTOM-SHEET PICKER ---------------- */
function MobilePickerSheet({
  open,
  title,
  items,
  selectedId,
  onPick,
  onClose,
  renderRow,
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [open]);

  if (!open) return null;

  return (
    <div style={sheetStyles.backdrop} onClick={onClose} role="button">
      <div
        style={sheetStyles.sheet}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div style={sheetStyles.header}>
          <div style={sheetStyles.title}>{title}</div>
          <button type="button" style={sheetStyles.close} onClick={onClose}>
            ✕
          </button>
        </div>

        <div style={sheetStyles.list}>
          {(items || []).map((it) => {
            const id = String(it?.id || "");
            const active = String(selectedId || "") === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => onPick(id)}
                style={{
                  ...sheetStyles.rowBtn,
                  borderColor: active ? "#2563eb" : "#e5e7eb",
                  background: active ? "#f5faff" : "#fff",
                }}
              >
                <div style={{ minWidth: 0, textAlign: "left" }}>
                  {renderRow ? (
                    renderRow(it)
                  ) : (
                    <div style={sheetStyles.rowMain}>{safe(it?.label) || id}</div>
                  )}
                </div>
                <div
                  style={{
                    fontWeight: 1000,
                    color: active ? "#2563eb" : "#94a3b8",
                  }}
                >
                  {active ? "✓" : ""}
                </div>
              </button>
            );
          })}
          {!items || !items.length ? (
            <div style={sheetStyles.empty}>No options available.</div>
          ) : null}
        </div>

        <div style={sheetStyles.footer}>
          <button type="button" style={sheetStyles.footerBtn} onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function MobilePickerButton({ valueText, placeholder, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...styles.pickBtn,
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <div style={{ minWidth: 0, textAlign: "left" }}>
        <div style={styles.pickBtnText}>{valueText || placeholder}</div>
      </div>
      <span style={{ color: "#94a3b8" }}>{Icons.ChevronDown}</span>
    </button>
  );
}

/* ---------------- POPUP MODAL (Phone + Reference) ---------------- */
function ConfirmPayModal({
  open,
  onClose,
  onConfirm,
  disabled,
  phone,
  setPhone,
  reference,
  setReference,
  chosenMethod,
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev || "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const type = safe(chosenMethod?.type).toUpperCase() || "MOMO";
  const label = safe(chosenMethod?.label) || type;
  const sub = chosenMethod ? formatMethodSub(chosenMethod) : "";

  return (
    <div style={modalStyles.backdrop} onClick={onClose} role="button">
      <div
        style={modalStyles.card}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div style={modalStyles.head}>
          <div style={modalStyles.headTitle}>Confirm Payment Details</div>
          <button type="button" style={modalStyles.close} onClick={onClose}>
            ✕
          </button>
        </div>

        <div style={modalStyles.body}>
          <div style={modalStyles.info}>
            Enter your <b>phone number</b> and the <b>Transaction ID / Reference</b>
            you used to pay the seller.
          </div>

          {chosenMethod ? (
            <div style={modalStyles.methodMini}>
              <div style={modalStyles.methodMiniIcon}>
                {safe(chosenMethod?.iconUrl) ? (
                  <img
                    src={chosenMethod.iconUrl}
                    alt={label}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <div style={{ fontSize: 16 }}>{iconEmojiFallback(type)}</div>
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={modalStyles.methodMiniTitle}>{label}</div>
                <div style={modalStyles.methodMiniSub}>{sub}</div>
              </div>
            </div>
          ) : null}

          <div style={{ display: "grid", gap: 10 }}>
            <label style={styles.label}>
              <div style={styles.labelText}>Phone Number</div>
              <div style={styles.inputWrap}>
                <span style={styles.leftIcon}>{Icons.Phone}</span>
                <input
                  style={styles.inputWithIcon}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+233 024xxxxxxx"
                  inputMode="tel"
                  disabled={disabled}
                />
              </div>
            </label>

            <label style={styles.label}>
              <div style={styles.labelText}>Transaction ID / Reference</div>
              <input
                style={styles.input}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="M123456789"
                disabled={disabled}
              />
            </label>
          </div>
        </div>

        <div style={modalStyles.foot}>
          <button
            type="button"
            style={modalStyles.ghost}
            onClick={onClose}
            disabled={disabled}
          >
            Cancel
          </button>
          <button
            type="button"
            style={modalStyles.primary}
            onClick={onConfirm}
            disabled={disabled}
          >
            Confirm & Place Order
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- POPUP MODAL (No Address) ---------------- */
function NoAddressModal({ open, onClose, onGoProfile }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev || "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div style={modalStyles.backdrop} onClick={onClose} role="button">
      <div
        style={modalStyles.card}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div style={modalStyles.head}>
          <div style={modalStyles.headTitle}>Add a Delivery Address</div>
          <button type="button" style={modalStyles.close} onClick={onClose}>
            ✕
          </button>
        </div>

        <div style={modalStyles.body}>
          <div style={modalStyles.info}>
            You don’t have any saved addresses yet. Please go to your profile and add an
            address, then come back to checkout.
          </div>
        </div>

        <div style={modalStyles.foot}>
          <button type="button" style={modalStyles.ghost} onClick={onClose}>
            Close
          </button>
          <button type="button" style={modalStyles.primary} onClick={onGoProfile}>
            Go to Profile → Addresses
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- component ---------------- */
export default function CheckoutScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state || {};

  // ✅ detect mode
  const productId = safe(state.productId); // Buy Now if present
  const isCartCheckout = !productId;

  const qtyBuyNow = Math.max(1, Math.min(99, Number(state.qty || 1)));
  const selectedColor = safe(state.selectedColor);
  const selectedSize = safe(state.selectedSize);

  const [user, setUser] = useState(() => auth.currentUser);

  // ✅ storeId from state OR query (?store or ?storeId)
  const storeIdFromQuery = useMemo(() => {
    try {
      const sp = new URLSearchParams(location?.search || "");
      return safe(sp.get("store")) || safe(sp.get("storeId"));
    } catch {
      return "";
    }
  }, [location?.search]);

  const initialStoreId = safe(state.storeId) || storeIdFromQuery;

  // store
  const [storeId, setStoreId] = useState(initialStoreId);
  const [storeOwnerId, setStoreOwnerId] = useState("");
  const [storeName, setStoreName] = useState("");

  // cart (for store)
  const [cart, setCart] = useState(() => loadCart(initialStoreId));

  // products map for cart checkout
  const [productMap, setProductMap] = useState({}); // { [id]: product }
  const [product, setProduct] = useState(null); // used for Buy Now

  // loaders
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [selectedDeliveryId, setSelectedDeliveryId] = useState("");

  // addresses
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const selectedAddress = useMemo(
    () => addresses.find((a) => a.id === selectedAddressId) || null,
    [addresses, selectedAddressId]
  );

  // buyer form
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState(""); // ✅ now filled in popup
  const [note, setNote] = useState("");

  // payment (PAY FIRST)
  const [chosenMethodId, setChosenMethodId] = useState("");
  const [reference, setReference] = useState(""); // ✅ now filled in popup
  const [proofUrl, setProofUrl] = useState("");
  const [proofUploading, setProofUploading] = useState(false);
  const [proofProgress, setProofProgress] = useState(0);

  const [placing, setPlacing] = useState(false);
  const [orderId, setOrderId] = useState("");

  const fileInputRef = useRef(null);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 980px)");
    const apply = () => setIsMobile(!!mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  // bottom-sheet state
  const [sheet, setSheet] = useState({ open: false, type: "" }); // type: "address" | "delivery"

  // ✅ popup state
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ✅ no-address popup
  const [noAddrOpen, setNoAddrOpen] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, []);

  // keep cart saved
  useEffect(() => {
    saveCart(storeId, cart);
  }, [storeId, cart]);

  // sync across tabs (per-store key)
  useEffect(() => {
    if (!safe(storeId)) return;
    const key = cartKey(storeId);
    const onStorage = (e) => {
      if (e?.key === key) setCart(loadCart(storeId));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storeId]);

  // load addresses
  useEffect(() => {
    let alive = true;

    async function loadAddresses() {
      setAddressesLoading(true);
      try {
        const u = auth.currentUser;
        if (!u) {
          if (alive) {
            setAddresses([]);
            setSelectedAddressId("");
          }
          return;
        }

        let defaultAddressId = "";
        try {
          const us = await getDoc(doc(db, "users", u.uid));
          defaultAddressId = us.exists() ? safe(us.data()?.defaultAddressId) : "";
        } catch {}

        const q1 = query(
          collection(db, "addresses"),
          where("userId", "==", u.uid),
          orderBy("createdAt", "desc"),
          limit(50)
        );
        const snap = await getDocs(q1);
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

        if (!alive) return;

        setAddresses(list);

        if (defaultAddressId && list.some((x) => x.id === defaultAddressId)) {
          setSelectedAddressId(defaultAddressId);
        } else if (list[0]?.id) {
          setSelectedAddressId(list[0].id);
        } else {
          setSelectedAddressId("");
        }
      } catch {
        if (!alive) return;
        setAddresses([]);
        setSelectedAddressId("");
      } finally {
        if (alive) setAddressesLoading(false);
      }
    }

    loadAddresses();
    return () => {
      alive = false;
    };
  }, [user?.uid]);

  // ✅ MAIN LOAD: store meta + payment/delivery + product(s)
  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setMsg("");
      setOrderId("");

      setPaymentMethods([]);
      setDeliveryOptions([]);
      setSelectedDeliveryId("");
      setChosenMethodId("");

      setReference("");
      setProofUrl("");
      setProofUploading(false);
      setProofProgress(0);

      setStoreOwnerId("");
      setStoreName("");

      const sid = safe(storeId) || safe(initialStoreId);
      if (!sid) {
        setMsg("Missing store. Go back and try again.");
        setLoading(false);
        return;
      }
      if (sid !== storeId) setStoreId(sid);

      const cartNow = loadCart(sid);
      setCart(cartNow);

      try {
        const stSnap = await getDoc(doc(db, "stores", sid));
        const st = stSnap.exists() ? stSnap.data() : null;
        if (alive) {
          setStoreOwnerId(safe(st?.ownerId));
          setStoreName(safe(st?.storeName));
        }
      } catch {
        if (alive) {
          setStoreOwnerId("");
          setStoreName("");
        }
      }

      const [pm, del] = await Promise.all([
        loadStorePaymentMethods(sid),
        loadStoreDeliveryOptions(sid),
      ]);
      if (!alive) return;

      const pmList = pm || [];
      const delList = del || [];

      setPaymentMethods(pmList);
      setDeliveryOptions(delList);

      if (pmList[0]?.id) setChosenMethodId(pmList[0].id);
      if (delList[0]?.id) setSelectedDeliveryId(delList[0].id);

      const u = auth.currentUser;
      if (u && !safe(fullName)) setFullName(safe(u.displayName) || "");

      if (!isCartCheckout) {
        if (!productId) {
          setMsg("Missing product. Go back and try again.");
          setLoading(false);
          return;
        }
        try {
          const snap = await getDoc(doc(db, "products", productId));
          if (!snap.exists()) {
            setMsg("Product not found.");
            setLoading(false);
            return;
          }
          const data = { id: snap.id, ...(snap.data() || {}) };
          if (!alive) return;
          setProduct(data);
          setProductMap({});
          setLoading(false);
          return;
        } catch (e) {
          if (!alive) return;
          setMsg(e?.message || "Failed to load product.");
          setLoading(false);
          return;
        }
      }

      const ids = (cartNow || []).map((x) => safe(x?.productId)).filter(Boolean);

      if (!ids.length) {
        setProduct(null);
        setProductMap({});
        setMsg("Your cart is empty. Go back and add items.");
        setLoading(false);
        return;
      }

      const results = {};
      for (const id of ids) {
        try {
          const ps = await getDoc(doc(db, "products", id));
          if (ps.exists()) results[id] = { id, ...(ps.data() || {}) };
        } catch {}
      }

      if (!alive) return;
      setProduct(null);
      setProductMap(results);
      setLoading(false);
    }

    load();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, storeIdFromQuery]);

  // whenever address changes, auto-pick best delivery
  useEffect(() => {
    if (!deliveryOptions?.length) return;
    const best = pickBestDelivery(deliveryOptions, selectedAddress);
    if (best?.id && best.id !== selectedDeliveryId) setSelectedDeliveryId(best.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddressId, deliveryOptions?.length]);

  // ✅ cart rows (for cart checkout)
  const cartRows = useMemo(() => {
    if (!isCartCheckout) return [];
    return (cart || [])
      .map((it) => {
        const pid = safe(it?.productId);
        const qty = Math.max(1, Math.min(99, Number(it?.qty || 1)));
        const p = productMap?.[pid] || null;
        return { productId: pid, qty, product: p };
      })
      .filter((r) => r.productId);
  }, [isCartCheckout, cart, productMap]);

  // currency (prefer first available product)
  const cur = useMemo(() => {
    if (!isCartCheckout) return safe(product?.currency) || "GHS";
    const first = cartRows.find((r) => r.product && safe(r.product.currency));
    return safe(first?.product?.currency) || "GHS";
  }, [isCartCheckout, product, cartRows]);

  const chosenMethod = useMemo(
    () => paymentMethods.find((m) => m.id === chosenMethodId) || null,
    [paymentMethods, chosenMethodId]
  );

  const selectedDelivery = useMemo(
    () => deliveryOptions.find((d) => d.id === selectedDeliveryId) || null,
    [deliveryOptions, selectedDeliveryId]
  );

  // ✅ subtotal
  const subtotal = useMemo(() => {
    if (!isCartCheckout) {
      const priceVal = getPriceValue(product) || 0;
      return Number(qtyBuyNow || 1) * Number(priceVal || 0);
    }

    let sum = 0;
    cartRows.forEach((r) => {
      const p = r.product;
      const unit = getPriceValue(p);
      sum += Number(unit || 0) * Number(r.qty || 1);
    });
    return sum;
  }, [isCartCheckout, product, qtyBuyNow, cartRows]);

  const deliveryFee = useMemo(() => {
    const pm = deliveryPricingMode(selectedDelivery);
    if (pm === "QUOTE") return 0;
    return Number(selectedDelivery?.fee || 0) || 0;
  }, [selectedDelivery]);

  const total = useMemo(() => subtotal + deliveryFee, [subtotal, deliveryFee]);

  // thumbnail for header card (use first item)
  const thumb = useMemo(() => {
    if (!isCartCheckout) {
      return (
        safe(product?.thumbnailUrl) ||
        safe(product?.imageUrl) ||
        safe((product?.mediaUrls || [])[0])
      );
    }
    const first = cartRows[0]?.product;
    return (
      safe(first?.thumbnailUrl) ||
      safe(first?.imageUrl) ||
      safe((first?.mediaUrls || [])[0])
    );
  }, [isCartCheckout, product, cartRows]);

  const cartBadge = useMemo(() => cartCount(cart), [cart]);

  /* ---------------- PROOF UPLOAD (NO orderId needed) ---------------- */
  const onPickProof = () => {
    if (proofUploading) return;
    fileInputRef.current?.click();
  };

  const handleProofFile = async (file) => {
    if (!file) return;

    const u = user;
    if (!u) return setMsg("Please login first.");

    setMsg("");
    setProofUploading(true);
    setProofProgress(0);

    try {
      const ext = safe(file.name).split(".").pop() || "jpg";
      const path = `tempProofs/${u.uid}/${Date.now()}.${ext}`;

      const storageRef = ref(storage, path);
      const task = uploadBytesResumable(storageRef, file);

      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => {
            const pct =
              snap.totalBytes > 0
                ? (snap.bytesTransferred / snap.totalBytes) * 100
                : 0;
            setProofProgress(Math.round(pct));
          },
          (err) => reject(err),
          () => resolve()
        );
      });

      const url = await getDownloadURL(task.snapshot.ref);
      setProofUrl(url);
      setMsg("✅ Proof uploaded.");
    } catch (e) {
      setMsg(e?.message || "Failed to upload proof.");
    } finally {
      setProofUploading(false);
    }
  };

  /* ---------------- PLACE ORDER (after they paid) ---------------- */
  const placeOrder = async () => {
    const u = user;
    if (!u) return setMsg("Please login first.");

    if (!safe(fullName)) return setMsg("Enter your name.");
    if (!safe(phone)) return setMsg("Enter your phone number.");
    if (!selectedDelivery) return setMsg("Select delivery option.");
    if (!selectedAddress) return setMsg("Select your delivery address.");
    if (!paymentMethods?.length) return setMsg("No payment methods for this store.");
    if (!chosenMethod) return setMsg("Select payment method.");
    if (!safe(reference)) return setMsg("Enter Transaction ID / Reference.");

    if (isCartCheckout) {
      if (!cartRows.length) return setMsg("Your cart is empty.");
      const anyMissing = cartRows.some((r) => !r.product);
      if (anyMissing)
        return setMsg("Some cart items could not be loaded. Go back and try again.");
    } else {
      if (!product) return setMsg("Product missing.");
    }

    setPlacing(true);
    setMsg("");

    try {
      const type = safe(chosenMethod?.type).toUpperCase() || "MOMO";
      const label = safe(chosenMethod?.label) || type;

      const itemsSnapshot = isCartCheckout
        ? cartRows.map((r) => {
            const p = r.product || {};
            const pcurThumb =
              safe(p?.thumbnailUrl) ||
              safe(p?.imageUrl) ||
              safe((p?.mediaUrls || [])[0]) ||
              "";

            const cartItem =
              (cart || []).find((x) => safe(x?.productId) === safe(r?.productId)) ||
              {};

            const color =
              safe(cartItem?.selectedColor) ||
              safe(cartItem?.color) ||
              safe(cartItem?.options?.color);

            const size =
              safe(cartItem?.selectedSize) ||
              safe(cartItem?.size) ||
              safe(cartItem?.options?.size);

            return {
              productId: safe(p?.id) || safe(r.productId),
              title: safe(p?.title),
              thumbnailUrl: pcurThumb,
              unitPrice: Number(getPriceValue(p) || 0),
              qty: Number(r.qty || 1),
              options: { color, size },
            };
          })
        : [];

      const singleSnapshot = !isCartCheckout
        ? {
            productId: product.id,
            title: safe(product?.title),
            thumbnailUrl: thumb,
            unitPrice: Number(getPriceValue(product) || 0),
            qty: Number(qtyBuyNow || 1),
            options: { color: selectedColor, size: selectedSize },
          }
        : null;

      const refDoc = await addDoc(collection(db, "orders"), {
        buyerId: u.uid,
        buyer: {
          uid: u.uid,
          name: safe(fullName),
          phone: safe(phone),
          note: safe(note),
        },

        storeId: safe(storeId),
        storeOwnerId: safe(storeOwnerId),
        storeName: safe(storeName),

        subtotal: Number(subtotal || 0),
        total: Number(total || 0),
        currency: cur,

        delivery: {
          optionId: safe(selectedDelivery?.id),
          type: safe(selectedDelivery?.type) || "DELIVERY",
          title: safe(selectedDelivery?.title),
          pricingMode: deliveryPricingMode(selectedDelivery),
          fee: Number(deliveryFee || 0),
          etaText: safe(selectedDelivery?.etaText),
          areas: Array.isArray(selectedDelivery?.areas) ? selectedDelivery.areas : [],
        },

        address: {
          addressId: selectedAddress.id,
          label: safe(selectedAddress.label),
          fullName: safe(selectedAddress.fullName),
          phone: safe(selectedAddress.phone),
          region: safe(selectedAddress.region),
          city: safe(selectedAddress.city),
          addressLine1: safe(selectedAddress.addressLine1),
          addressLine2: safe(selectedAddress.addressLine2),
          landmark: safe(selectedAddress.landmark),
        },

        payment: {
          note: "Buyer paid seller outside platform and submitted reference.",
          status: "PENDING_CONFIRMATION",
          type,
          methodId: chosenMethod.id,
          label,
          reference: safe(reference),
          proofUrl: safe(proofUrl),
          paidAt: serverTimestamp(),
        },

        itemsSnapshot: isCartCheckout ? itemsSnapshot : [],
        productSnapshot: isCartCheckout ? null : singleSnapshot,

        status: "PENDING_CONFIRMATION",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (isCartCheckout) {
        saveCart(storeId, []);
        setCart([]);
      }

      setOrderId(refDoc.id);
      setMsg("✅ Order placed. Waiting for seller confirmation.");
    } catch (e) {
      setMsg(e?.message || "Failed to place order.");
    } finally {
      setPlacing(false);
      setConfirmOpen(false);
    }
  };

  const addressValueText = useMemo(() => {
    const a = addresses.find((x) => x.id === selectedAddressId);
    return a ? formatAddressOneLine(a) : "";
  }, [addresses, selectedAddressId]);

  const deliveryValueText = useMemo(() => {
    const d = deliveryOptions.find((x) => x.id === selectedDeliveryId);
    if (!d) return "";
    const pm = deliveryPricingMode(d);
    const areaLabel =
      Array.isArray(d?.areas) && d.areas.length
        ? ` (${d.areas.slice(0, 3).join(", ")}${d.areas.length > 3 ? "…" : ""})`
        : "";
    const feeText = pm === "QUOTE" ? " • QUOTE" : ` • ${money(Number(d?.fee || 0), cur)}`;
    return (safe(d?.title) || safe(d?.type) || "Delivery") + areaLabel + feeText;
  }, [deliveryOptions, selectedDeliveryId, cur]);

  // ✅ summary header title + qty
  const summaryTitle = useMemo(() => {
    if (!isCartCheckout) return safe(product?.title) || "Product";
    const count = cartRows.length;
    if (count === 1) return safe(cartRows[0]?.product?.title) || "Item";
    return `${count} items`;
  }, [isCartCheckout, product, cartRows]);

  const summaryQtyText = useMemo(() => {
    if (!isCartCheckout) return `Qty: ${qtyBuyNow}`;
    return `Items: ${cartRows.length} • Qty: ${cartBadge}`;
  }, [isCartCheckout, qtyBuyNow, cartRows.length, cartBadge]);

  // ✅ button click opens popup instead of placing immediately
  const onClickPlace = () => {
    if (placing || orderId) return;

    // quick pre-checks before popup (so popup doesn't open for obvious missing stuff)
    if (!safe(fullName)) return setMsg("Enter your name.");

    // ✅ NEW: if no addresses, show popup
    if (!addressesLoading && (!addresses || !addresses.length)) {
      setMsg("");
      setNoAddrOpen(true);
      return;
    }

    if (!selectedDelivery) return setMsg("Select delivery option.");
    if (!selectedAddress) return setMsg("Select your delivery address.");
    if (!paymentMethods?.length) return setMsg("No payment methods for this store.");
    if (!chosenMethod) return setMsg("Select payment method.");
    if (isCartCheckout) {
      if (!cartRows.length) return setMsg("Your cart is empty.");
      const anyMissing = cartRows.some((r) => !r.product);
      if (anyMissing)
        return setMsg("Some cart items could not be loaded. Go back and try again.");
    } else {
      if (!product) return setMsg("Product missing.");
    }

    setMsg("");
    setConfirmOpen(true);
  };

  return (
    <div style={styles.page}>
      <div
        style={{
          ...styles.overlay,
          padding: isMobile ? 0 : styles.overlay.padding,
          placeItems: isMobile ? "start" : "center",
        }}
      >
        <div
          style={{
            ...styles.modal,
            width: isMobile ? "100vw" : styles.modal.width,
            height: isMobile ? "100vh" : styles.modal.height,
            maxHeight: isMobile ? "100vh" : styles.modal.maxHeight,
            borderRadius: isMobile ? 0 : styles.modal.borderRadius,
            border: isMobile ? 0 : styles.modal.border,
          }}
        >
          {/* header */}
          <div
            style={{
              ...styles.modalHeader,
              padding: isMobile ? "12px 12px 10px" : styles.modalHeader.padding,
            }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div
                style={{
                  ...styles.headerIcon,
                  width: isMobile ? 30 : 34,
                  height: isMobile ? 30 : 34,
                }}
              >
                💳
              </div>
              <div>
                <div style={{ ...styles.hTitle, fontSize: isMobile ? 18 : 20 }}>
                  Pay &amp; Place Order
                </div>
                <div style={{ ...styles.hSub, fontSize: isMobile ? 12 : 13 }}>
                  Pay first, then confirm your phone + reference to place the order.
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate(-1)}
              style={styles.closeBtn}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* body */}
          <div
            style={{
              ...styles.body,
              padding: isMobile ? 12 : styles.body.padding,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {loading ? <div style={styles.alert}>Loading…</div> : null}
            {msg ? <div style={styles.alert}>{msg}</div> : null}

            {!loading && (!isCartCheckout ? product : cartRows.length > 0) ? (
              <div
                style={{
                  ...styles.grid,
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                }}
              >
                {/* LEFT */}
                <div style={{ display: "grid", gap: 12 }}>
                  <div style={styles.section}>
                    <div
                      style={{
                        ...styles.sectionTitle,
                        fontSize: isMobile ? 15 : 16,
                      }}
                    >
                      Payment (Pay first)
                    </div>

                    <div style={styles.infoBox}>
                      Choose a method, pay the seller, and (optional) upload proof.
                      When you tap <b>Place Order</b>, we’ll ask for your phone + reference in a popup.
                    </div>

                    <div style={{ display: "grid", gap: 10 }}>
                      {(paymentMethods || []).length ? (
                        paymentMethods.map((m) => {
                          const right =
                            safe(m?.rightText) || formatMethodRightTextAuto(m);
                          const isOn = chosenMethodId === m.id;
                          return (
                            <div
                              key={m.id}
                              onClick={() => !orderId && setChosenMethodId(m.id)}
                              role="button"
                              style={{
                                ...styles.methodCard,
                                borderColor: isOn ? "#2563eb" : "#e5e7eb",
                                background: isOn ? "#f5faff" : "#ffffff",
                                opacity: orderId ? 0.9 : 1,
                                cursor: orderId ? "default" : "pointer",
                              }}
                            >
                              <div style={styles.methodLeft}>
                                <div style={styles.methodIconImgWrap}>
                                  {safe(m?.iconUrl) ? (
                                    <img
                                      src={m.iconUrl}
                                      alt={safe(m?.label) || "method"}
                                      style={styles.methodIconImg}
                                    />
                                  ) : (
                                    <div style={styles.methodIconFallback}>
                                      {iconEmojiFallback(m?.type)}
                                    </div>
                                  )}
                                </div>

                                <div>
                                  <div style={styles.methodTitle}>
                                    {safe(m?.label) || safe(m?.type) || "Method"}
                                  </div>
                                  <div style={styles.methodSub}>
                                    {formatMethodSub(m)}
                                  </div>
                                </div>
                              </div>

                              <div style={styles.methodRight}>{right}</div>
                            </div>
                          );
                        })
                      ) : (
                        <div style={styles.alert}>
                          No payment methods found for this store. Seller must add
                          payment methods.
                        </div>
                      )}
                    </div>

                    <label style={{ ...styles.label, marginTop: 10 }}>
                      <div style={styles.labelText}>Upload Screenshot (optional)</div>

                      <div
                        style={{
                          ...styles.uploadBox,
                          cursor:
                            proofUploading || orderId ? "not-allowed" : "pointer",
                          opacity: proofUploading || orderId ? 0.85 : 1,
                        }}
                        onClick={() => {
                          if (!orderId) onPickProof();
                        }}
                        role="button"
                      >
                        <div style={{ color: "#94a3b8" }}>{Icons.Upload}</div>
                        <div style={styles.uploadText}>
                          {orderId
                            ? "Order already placed."
                            : proofUploading
                            ? `Uploading… ${proofProgress}%`
                            : proofUrl
                            ? "✅ Proof uploaded (tap to replace)"
                            : "Click to upload image proof…"}
                        </div>
                      </div>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleProofFile(e.target.files?.[0] || null)}
                        disabled={!!orderId}
                      />

                      {proofUrl ? (
                        <div style={styles.proofPreviewWrap}>
                          <img src={proofUrl} alt="proof" style={styles.proofPreview} />
                        </div>
                      ) : null}
                    </label>
                  </div>

                  <div style={styles.section}>
                    <div
                      style={{
                        ...styles.sectionTitle,
                        fontSize: isMobile ? 15 : 16,
                      }}
                    >
                      Buyer Details
                    </div>

                    <div style={{ display: "grid", gap: 10 }}>
                      <InputRow
                        icon={Icons.User}
                        placeholder="Ama Mensah"
                        value={fullName}
                        onChange={setFullName}
                        inputMode="text"
                        disabled={!!orderId}
                      />

                      <label style={styles.label}>
                        <div style={styles.labelText}>Note (optional)</div>
                        <div style={styles.textareaWrap}>
                          <span style={styles.leftIcon}>{Icons.Note}</span>
                          <textarea
                            style={styles.textarea}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Note (optional)"
                            disabled={!!orderId}
                          />
                        </div>
                      </label>
                    </div>

                    {/* small hint showing current phone/reference values (optional) */}
                    {(safe(phone) || safe(reference)) && !orderId ? (
                      <div style={{ ...styles.smallWarn, marginTop: 12 }}>
                        <b>Saved:</b>{" "}
                        {safe(phone) ? `Phone: ${safe(phone)}` : ""}
                        {safe(phone) && safe(reference) ? " • " : ""}
                        {safe(reference) ? `Ref: ${safe(reference)}` : ""}
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* RIGHT */}
                <div style={{ display: "grid", gap: 12 }}>
                  <div style={styles.section}>
                    <div
                      style={{
                        ...styles.sectionTitle,
                        fontSize: isMobile ? 15 : 16,
                      }}
                    >
                      Order Summary
                    </div>

                    <div style={styles.summaryCard}>
                      <div style={styles.summaryTop}>
                        <div style={styles.productThumb}>
                          {thumb ? (
                            <img alt="thumb" src={thumb} style={styles.thumbImg} />
                          ) : (
                            <div style={styles.thumbPh}>🟧</div>
                          )}
                        </div>

                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              ...styles.productName,
                              fontSize: isMobile ? 13 : 14,
                            }}
                          >
                            {summaryTitle}
                          </div>
                          <div style={styles.graySmall}>{summaryQtyText}</div>
                        </div>

                        <div style={styles.priceRight}>{money(subtotal, cur)}</div>
                      </div>

                      {/* ✅ show all cart items (only in cart checkout) */}
                      {isCartCheckout ? (
                        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                          {cartRows.map((r) => {
                            const p = r.product || {};
                            const title = safe(p?.title) || "Item";
                            const unit = getPriceValue(p) || 0;
                            const line = Number(unit) * Number(r.qty || 1);
                            return (
                              <div
                                key={r.productId}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: 10,
                                  color: "#0f172a",
                                  fontWeight: 900,
                                  fontSize: 12,
                                }}
                              >
                                <div
                                  style={{
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {title}{" "}
                                  <span style={{ color: "#64748b", fontWeight: 800 }}>
                                    × {r.qty}
                                  </span>
                                </div>
                                <div>{money(line, cur)}</div>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}

                      <div style={styles.hr} />

                      {/* ✅ Delivery Address Picker */}
                      <div style={styles.row}>
                        <div style={styles.rowLabel}>Delivery Address</div>
                        <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                          {isMobile ? (
                            <MobilePickerButton
                              valueText={addressValueText}
                              placeholder={addressesLoading ? "Loading…" : "Choose address"}
                              disabled={addressesLoading || !!orderId}
                              onClick={() => {
                                if (addressesLoading || orderId) return;
                                if (!addresses?.length) return setNoAddrOpen(true);
                                setSheet({ open: true, type: "address" });
                              }}
                            />
                          ) : (
                            <select
                              value={selectedAddressId}
                              onChange={(e) => setSelectedAddressId(e.target.value)}
                              style={{ ...styles.select, maxWidth: 320 }}
                              disabled={addressesLoading || !addresses.length || !!orderId}
                              onClick={(e) => {
                                if (!addressesLoading && !orderId && !addresses?.length) {
                                  e.preventDefault();
                                  setNoAddrOpen(true);
                                }
                              }}
                            >
                              {addresses.map((a) => (
                                <option key={a.id} value={a.id}>
                                  {formatAddressOneLine(a) || "Address"}
                                </option>
                              ))}
                              {!addresses.length ? (
                                <option value="">No saved addresses</option>
                              ) : null}
                            </select>
                          )}
                        </div>
                      </div>

                      {/* ✅ Delivery Option Picker */}
                      <div style={styles.row}>
                        <div style={styles.rowLabel}>Delivery Option</div>
                        <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                          {isMobile ? (
                            <MobilePickerButton
                              valueText={deliveryValueText}
                              placeholder={!deliveryOptions?.length ? "No options" : "Choose option"}
                              disabled={!deliveryOptions?.length || !!orderId}
                              onClick={() => setSheet({ open: true, type: "delivery" })}
                            />
                          ) : (
                            <select
                              value={selectedDeliveryId}
                              onChange={(e) => setSelectedDeliveryId(e.target.value)}
                              style={{ ...styles.select, maxWidth: 320 }}
                              disabled={!!orderId}
                            >
                              {(deliveryOptions || []).map((d) => {
                                const pm = deliveryPricingMode(d);
                                const areaLabel =
                                  Array.isArray(d?.areas) && d.areas.length
                                    ? ` (${d.areas.slice(0, 3).join(", ")}${d.areas.length > 3 ? "…" : ""})`
                                    : "";
                                const feeText =
                                  pm === "QUOTE"
                                    ? " • QUOTE"
                                    : ` • ${money(Number(d?.fee || 0), cur)}`;
                                return (
                                  <option key={d.id} value={d.id}>
                                    {(safe(d?.title) || safe(d?.type) || "Delivery") +
                                      areaLabel +
                                      feeText}
                                  </option>
                                );
                              })}
                              {!deliveryOptions?.length ? (
                                <option value="">No delivery options</option>
                              ) : null}
                            </select>
                          )}
                        </div>
                      </div>

                      {selectedDelivery &&
                      selectedAddress &&
                      !deliveryMatchesAddress(selectedDelivery, selectedAddress) ? (
                        <div style={{ ...styles.smallWarn, marginTop: 10 }}>
                          ⚠️ This delivery option may not match your address area. Please choose a
                          better one.
                        </div>
                      ) : null}

                      <div style={styles.row}>
                        <div style={styles.rowLabel}>Subtotal</div>
                        <div style={styles.rowValue}>{money(subtotal, cur)}</div>
                      </div>

                      <div style={styles.row}>
                        <div style={styles.rowLabel}>Delivery fee</div>
                        <div style={styles.rowValue}>
                          {deliveryPricingMode(selectedDelivery) === "QUOTE"
                            ? "QUOTE (decided later)"
                            : money(deliveryFee, cur)}
                        </div>
                      </div>

                      <div style={{ ...styles.row, marginTop: 6 }}>
                        <div style={styles.totalLabel}>Total</div>
                        <div style={styles.totalValue}>{money(total, cur)}</div>
                      </div>
                    </div>

                    {/* ✅ Place order opens popup */}
                    <button
                      type="button"
                      onClick={onClickPlace}
                      disabled={placing || !!orderId}
                      style={{
                        ...styles.primaryBtn,
                        marginTop: 12,
                        width: "100%",
                        opacity: placing || orderId ? 0.7 : 1,
                        cursor: placing || orderId ? "not-allowed" : "pointer",
                      }}
                    >
                      {orderId ? "Order Placed ✅" : placing ? "Placing…" : "Place Order (I’ve Paid)"}
                    </button>

                    {/* ✅ Success message BELOW button */}
                    {orderId ? (
                      <div style={styles.successBox}>
                        <div style={styles.successTitle}>✅ Order placed!</div>
                        <div style={styles.successSub}>
                          Waiting for seller confirmation.
                        </div>

                        <div style={{ ...styles.orderIdRow, justifyContent: "flex-start", marginTop: 10 }}>
                          <span style={styles.orderIdLabel}>Order ID:</span>
                          <span style={styles.orderIdValue}>{orderId}</span>
                        </div>

                        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
                          <button type="button" onClick={() => navigate(-1)} style={styles.ghostBtn}>
                            Close
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* ✅ Bottom-sheet pickers */}
                <MobilePickerSheet
                  open={sheet.open && sheet.type === "address"}
                  title="Choose Delivery Address"
                  items={addresses}
                  selectedId={selectedAddressId}
                  onPick={(id) => {
                    setSelectedAddressId(id);
                    setSheet({ open: false, type: "" });
                  }}
                  onClose={() => setSheet({ open: false, type: "" })}
                  renderRow={(a) => (
                    <>
                      <div style={sheetStyles.rowMain}>{safe(a?.label) || "Address"}</div>
                      <div style={sheetStyles.rowSub}>{formatAddressOneLine(a)}</div>
                    </>
                  )}
                />

                <MobilePickerSheet
                  open={sheet.open && sheet.type === "delivery"}
                  title="Choose Delivery Option"
                  items={deliveryOptions}
                  selectedId={selectedDeliveryId}
                  onPick={(id) => {
                    setSelectedDeliveryId(id);
                    setSheet({ open: false, type: "" });
                  }}
                  onClose={() => setSheet({ open: false, type: "" })}
                  renderRow={(d) => {
                    const pm = deliveryPricingMode(d);
                    const areaLabel =
                      Array.isArray(d?.areas) && d.areas.length
                        ? ` (${d?.areas?.slice(0, 3).join(", ")}${d.areas.length > 3 ? "…" : ""})`
                        : "";
                    const feeText =
                      pm === "QUOTE" ? " • QUOTE" : ` • ${money(Number(d?.fee || 0), cur)}`;
                    const top =
                      (safe(d?.title) || safe(d?.type) || "Delivery") + areaLabel + feeText;

                    const eta = safe(d?.etaText);
                    return (
                      <>
                        <div style={sheetStyles.rowMain}>{top}</div>
                        {eta ? <div style={sheetStyles.rowSub}>ETA: {eta}</div> : null}
                      </>
                    );
                  }}
                />

                {/* ✅ Popup for Phone + Reference */}
                <ConfirmPayModal
                  open={confirmOpen}
                  onClose={() => setConfirmOpen(false)}
                  onConfirm={placeOrder}
                  disabled={placing || !!orderId}
                  phone={phone}
                  setPhone={setPhone}
                  reference={reference}
                  setReference={setReference}
                  chosenMethod={chosenMethod}
                />

                {/* ✅ Popup for NO ADDRESS */}
                <NoAddressModal
                  open={noAddrOpen}
                  onClose={() => setNoAddrOpen(false)}
                  onGoProfile={() => {
                    setNoAddrOpen(false);
                    navigate("/me"); // profile screen (where they can add address)
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- small components ---------------- */
function InputRow({ icon, value, onChange, placeholder, inputMode, disabled }) {
  return (
    <label style={styles.label}>
      <div style={styles.inputWrap}>
        <span style={styles.leftIcon}>{icon}</span>
        <input
          style={{ ...styles.inputWithIcon, opacity: disabled ? 0.85 : 1 }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          inputMode={inputMode}
          disabled={disabled}
        />
      </div>
    </label>
  );
}

/* ---------------- styles ---------------- */
const styles = {
  page: {
    minHeight: "100vh",
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
    overflowX: "hidden",
  },
  overlay: {
    minHeight: "100vh",
    padding: 18,
    background: "rgba(15, 23, 42, 0.55)",
    display: "grid",
    placeItems: "center",
  },

  modal: {
    width: "min(1120px, 96vw)",
    height: "auto",
    maxHeight: "92vh",
    background: "#fff",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 30px 90px rgba(0,0,0,0.35)",
    border: "1px solid rgba(255,255,255,0.35)",
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
  },

  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "18px 18px 12px",
    borderBottom: "1px solid #eef2f7",
    background: "#f8fafc",
    flex: "0 0 auto",
  },
  headerIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: "#eef2ff",
    display: "grid",
    placeItems: "center",
    fontSize: 18,
  },
  hTitle: { fontWeight: 1000, fontSize: 20, color: "#0f172a" },
  hSub: {
    marginTop: 4,
    color: "#64748b",
    fontWeight: 700,
    fontSize: 13,
    maxWidth: 720,
  },
  closeBtn: {
    border: 0,
    background: "transparent",
    fontSize: 20,
    cursor: "pointer",
    color: "#64748b",
    padding: 6,
    borderRadius: 10,
  },

  body: {
    padding: 18,
    background: "#fff",
    flex: "1 1 auto",
    minHeight: 0,
  },

  alert: {
    background: "#f1f5ff",
    border: "1px solid #dbeafe",
    color: "#0f172a",
    padding: 10,
    borderRadius: 10,
    fontWeight: 900,
    marginBottom: 10,
  },

  grid: { display: "grid", gap: 14 },

  section: {
    border: "1px solid #eef2f7",
    borderRadius: 12,
    padding: 14,
    background: "#fff",
  },
  sectionTitle: {
    fontWeight: 1000,
    color: "#0f172a",
    fontSize: 16,
    marginBottom: 10,
  },

  summaryCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 12,
  },
  summaryTop: {
    display: "grid",
    gridTemplateColumns: "54px 1fr auto",
    gap: 12,
    alignItems: "center",
  },
  productThumb: {
    width: 54,
    height: 54,
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #e5e7eb",
    background: "#f1f5f9",
    display: "grid",
    placeItems: "center",
  },
  thumbImg: { width: "100%", height: "100%", objectFit: "cover" },
  thumbPh: { fontSize: 22, opacity: 0.8 },
  productName: { fontWeight: 1000, fontSize: 14, color: "#0f172a" },
  graySmall: {
    color: "#64748b",
    fontWeight: 800,
    fontSize: 12,
    marginTop: 2,
  },
  priceRight: { fontWeight: 1000, color: "#0f172a" },

  hr: { height: 1, background: "#eef2f7", margin: "12px 0" },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginTop: 10,
  },
  rowLabel: { color: "#64748b", fontWeight: 800, fontSize: 13 },
  rowValue: { fontWeight: 1000, color: "#0f172a" },
  totalLabel: { fontWeight: 1100, color: "#0f172a" },
  totalValue: { fontWeight: 1200, fontSize: 18, color: "#0f172a" },

  label: { margin: 0 },
  labelText: {
    fontWeight: 900,
    color: "#64748b",
    fontSize: 12,
    marginBottom: 6,
  },

  inputWrap: { position: "relative", display: "flex", alignItems: "center" },
  leftIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#94a3b8",
    display: "grid",
    placeItems: "center",
  },
  inputWithIcon: {
    width: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "12px 12px 12px 42px",
    fontWeight: 900,
    outline: "none",
    color: "#0f172a",
    background: "#fff",
  },
  input: {
    width: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "12px 12px",
    fontWeight: 900,
    outline: "none",
    color: "#0f172a",
    background: "#fff",
  },
  textareaWrap: { position: "relative" },
  textarea: {
    width: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "12px 12px 12px 42px",
    fontWeight: 900,
    outline: "none",
    color: "#0f172a",
    background: "#fff",
    minHeight: 82,
    resize: "vertical",
  },

  select: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    fontWeight: 900,
    outline: "none",
    color: "#0f172a",
    background: "#fff",
    maxWidth: 320,
  },

  pickBtn: {
    width: "100%",
    minHeight: 44,
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    background: "#fff",
    fontWeight: 900,
    color: "#0f172a",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  pickBtnText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },

  infoBox: {
    background: "#f1f5f9",
    border: "1px solid #e5e7eb",
    padding: 12,
    borderRadius: 12,
    color: "#0f172a",
    fontWeight: 800,
    fontSize: 13,
    lineHeight: 1.35,
    marginBottom: 12,
  },

  primaryBtn: {
    border: 0,
    background: "#2563eb",
    color: "#fff",
    borderRadius: 10,
    padding: "12px 14px",
    fontWeight: 1000,
  },

  uploadBox: {
    border: "1px dashed #cbd5e1",
    borderRadius: 12,
    padding: 16,
    display: "grid",
    placeItems: "center",
    gap: 8,
    background: "#f8fafc",
  },
  uploadText: { color: "#64748b", fontWeight: 900 },

  proofPreviewWrap: {
    marginTop: 10,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    background: "#fff",
  },
  proofPreview: {
    width: "100%",
    height: 200,
    objectFit: "cover",
    display: "block",
  },

  methodCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    cursor: "pointer",
  },
  methodLeft: { display: "flex", gap: 10, alignItems: "center" },

  methodIconImgWrap: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: "#f1f5f9",
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
    border: "1px solid #e5e7eb",
  },
  methodIconImg: { width: "100%", height: "100%", objectFit: "cover" },
  methodIconFallback: {
    width: "100%",
    height: "100%",
    display: "grid",
    placeItems: "center",
    fontSize: 18,
  },

  methodTitle: { fontWeight: 1000, color: "#0f172a" },
  methodSub: { color: "#64748b", fontWeight: 800, fontSize: 12, marginTop: 2 },
  methodRight: { color: "#0f172a", fontWeight: 1000 },

  orderIdRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "flex-end",
    color: "#64748b",
    fontWeight: 900,
    fontSize: 12,
  },
  orderIdLabel: { color: "#64748b" },
  orderIdValue: { color: "#0f172a", fontWeight: 1000 },

  smallWarn: {
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    padding: 10,
    borderRadius: 12,
    color: "#0f172a",
    fontWeight: 800,
    fontSize: 12,
    lineHeight: 1.35,
  },

  // ✅ success panel
  successBox: {
    marginTop: 12,
    borderRadius: 14,
    border: "1px solid #bbf7d0",
    background: "#f0fdf4",
    padding: 14,
  },
  successTitle: { fontWeight: 1100, color: "#166534", fontSize: 14 },
  successSub: {
    marginTop: 6,
    color: "#166534",
    fontWeight: 800,
    fontSize: 12,
    lineHeight: 1.35,
  },
  ghostBtn: {
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#0f172a",
    borderRadius: 12,
    padding: "12px 14px",
    fontWeight: 1000,
    cursor: "pointer",
  },
};

const sheetStyles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.55)",
    zIndex: 9999,
    display: "grid",
    alignItems: "end",
  },
  sheet: {
    background: "#fff",
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    boxShadow: "0 -20px 60px rgba(0,0,0,0.25)",
    maxHeight: "78vh",
    overflow: "hidden",
  },
  header: {
    padding: "14px 14px 10px",
    borderBottom: "1px solid #eef2f7",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  title: { fontWeight: 1000, color: "#0f172a", fontSize: 15 },
  close: {
    border: 0,
    background: "transparent",
    fontSize: 18,
    color: "#64748b",
    padding: 8,
    borderRadius: 10,
    cursor: "pointer",
  },
  list: {
    padding: 12,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    maxHeight: "58vh",
    display: "grid",
    gap: 10,
  },
  rowBtn: {
    width: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 12,
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    cursor: "pointer",
  },
  rowMain: { fontWeight: 1000, color: "#0f172a", fontSize: 14 },
  rowSub: {
    marginTop: 4,
    color: "#64748b",
    fontWeight: 800,
    fontSize: 12,
    lineHeight: 1.25,
  },
  empty: { padding: 16, color: "#64748b", fontWeight: 900, textAlign: "center" },
  footer: {
    padding: 12,
    borderTop: "1px solid #eef2f7",
    background: "#f8fafc",
  },
  footerBtn: {
    width: "100%",
    border: 0,
    borderRadius: 12,
    padding: "12px 14px",
    background: "#2563eb",
    color: "#fff",
    fontWeight: 1000,
    cursor: "pointer",
  },
};

const modalStyles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.55)",
    zIndex: 10000,
    display: "grid",
    placeItems: "center",
    padding: 14,
  },
  card: {
    width: "min(520px, 96vw)",
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 30px 90px rgba(0,0,0,0.35)",
    border: "1px solid rgba(255,255,255,0.35)",
    overflow: "hidden",
  },
  head: {
    padding: "14px 14px 12px",
    borderBottom: "1px solid #eef2f7",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  headTitle: { fontWeight: 1000, color: "#0f172a" },
  close: {
    border: 0,
    background: "transparent",
    fontSize: 18,
    color: "#64748b",
    padding: 8,
    borderRadius: 10,
    cursor: "pointer",
  },
  body: { padding: 14 },
  info: {
    background: "#f1f5f9",
    border: "1px solid #e5e7eb",
    padding: 12,
    borderRadius: 12,
    color: "#0f172a",
    fontWeight: 800,
    fontSize: 13,
    lineHeight: 1.35,
    marginBottom: 12,
  },
  methodMini: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    padding: 10,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    marginBottom: 12,
    background: "#fff",
  },
  methodMiniIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: "#f1f5f9",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    display: "grid",
    placeItems: "center",
    flex: "0 0 auto",
  },
  methodMiniTitle: { fontWeight: 1000, color: "#0f172a" },
  methodMiniSub: { marginTop: 2, color: "#64748b", fontWeight: 800, fontSize: 12 },
  foot: {
    padding: 14,
    borderTop: "1px solid #eef2f7",
    background: "#fff",
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
  },
  ghost: {
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#0f172a",
    borderRadius: 12,
    padding: "12px 14px",
    fontWeight: 1000,
    cursor: "pointer",
  },
  primary: {
    border: 0,
    background: "#2563eb",
    color: "#fff",
    borderRadius: 12,
    padding: "12px 14px",
    fontWeight: 1000,
    cursor: "pointer",
  },
};
